<template>
  <div class="dark-theme-wrapper">
    <HamburgerMenu
      @navigate="navigateTo"
      :username="username"
      :profilePicture="profilePicture"
      :hasNotification="hasUnreadMessages"
    />

    <div class="container content-wrapper">
      <div class="header-section text-center fade-in">
        <div class="avatar-header-wrapper mb-3">
          <img
            v-if="profilePicture"
            :src="profilePicture"
            alt="Profil"
            class="header-avatar"
          />
          <div v-else class="header-avatar-placeholder">
            <svg
              class="placeholder-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </div>
        </div>

        <h2 class="welcome-text">
          VÍTEJTE, <span class="text-red">{{ username || "UŽIVATELI" }}</span>
        </h2>

        <div class="mini-stats-badges mb-3">
          <span class="badge-pill points">
            <svg
              class="icon-xs me-2"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
              <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
              <path d="M4 22h16"></path>
              <path
                d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"
              ></path>
              <path
                d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"
              ></path>
              <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
            </svg>
            {{ totalPoints || 0 }} BODŮ
          </span>
          <span class="badge-pill joker" :class="{ used: jokerGlobalUsed }">
            <svg
              class="icon-xs me-2"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polygon
                points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
              ></polygon>
            </svg>
            JOKER: {{ jokerGlobalUsed ? "VYUŽIT" : "AKTIVNÍ" }}
          </span>
        </div>

        <div
          v-if="!notificationsEnabled && isNotificationSupported"
          class="mb-3"
        >
          <button @click="enableNotifications" class="btn-notification">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path
                d="M8 16a2 2 0 0 0 2-2H6a2 2 0 0 0 2 2zm.995-14.901a1 1 0 1 0-1.99 0A5.002 5.002 0 0 0 3 6c0 1.098-.5 6-2 7h14c-1.5-1-2-5.902-2-7 0-2.42-1.72-4.44-4.005-4.901z"
              />
            </svg>
            <span>Zapnout upozornění, ať nezapomeneš tipovat</span>
          </button>
        </div>

        <div class="separator mx-auto"></div>
      </div>

      <transition name="fade" mode="out-in">
        <div v-if="currentView === 'dashboard'" key="dashboard">
          <div
            v-if="missingTipsCount > 0 || missingQuestion"
            class="alert-card warning mb-4 fade-in"
          >
            <div class="alert-icon">
              <svg
                viewBox="0 0 24 24"
                width="32"
                height="32"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
            </div>
            <div class="alert-content">
              <h4>Pozor! Nemáš kompletně natipováno!</h4>
              <p v-if="missingTipsCount > 0">
                Chybí ti tipy pro
                <strong>{{ missingTipsCount }} zápasy</strong>.
              </p>
              <p v-if="missingQuestion">
                Zatím jsi neodpověděl(a) na <strong>Bonusovou otázku</strong>.
              </p>
              <button
                @click="navigateTo('tipForm')"
                class="btn-warning-glow mt-2"
              >
                DOPLNIT TIPY
              </button>
            </div>
          </div>

          <div
            v-else-if="selectedRound !== null"
            class="alert-card success mb-4 fade-in"
          >
            <div class="alert-icon">
              <svg
                viewBox="0 0 24 24"
                width="32"
                height="32"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <div class="alert-content">
              <h4>Vše máš připraveno!</h4>
              <p>
                Tvé tipy pro {{ selectedRound }}. kolo jsou bezpečně uloženy.
              </p>
              <button
                @click="navigateTo('tipForm')"
                class="btn-success-glow mt-2"
              >
                UPRAVIT MOJE TIPY
              </button>
            </div>
          </div>

          <div v-if="hasUnreadMessages" class="alert-card info mb-5 fade-in">
            <div class="alert-icon">
              <svg
                viewBox="0 0 24 24"
                width="32"
                height="32"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                ></path>
              </svg>
            </div>
            <div class="alert-content">
              <h4>Nové zprávy v chatu</h4>
              <p>V komunitním chatu se něco děje. Nenech si to ujít!</p>
              <button @click="navigateTo('chat')" class="btn-info-glow mt-2">
                PŘEJÍT DO CHATU
              </button>
            </div>
          </div>

          <div class="mt-4">
            <h3 class="section-title text-center mb-4">STATISTIKY</h3>
            <TipStatistics ref="tipStats" :username="username" />
          </div>
        </div>

        <div v-else-if="currentView === 'tipForm'" key="tipForm">
          <div class="d-flex align-items-center justify-content-between mb-4">
            <button @click="navigateTo('dashboard')" class="btn-back">
              <svg
                class="icon-sm me-1"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              ZPĚT NA PŘEHLED
            </button>
            <h3 class="m-0 text-red font-weight-bold">
              TIPOVAČKA {{ selectedRound }}. KOLO
            </h3>
          </div>
          <TipForm
            v-if="selectedRound !== null"
            :roundNumber="selectedRound"
            :disabled="disableTips"
            @tips-submitted="onTipsSubmitted"
            @update-statistics="refreshStatistics"
          />
        </div>

        <div v-else-if="currentView === 'chat'" key="chat">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <button @click="navigateTo('dashboard')" class="btn-back">
              <svg
                class="icon-sm me-1"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              ZPĚT NA PŘEHLED
            </button>
          </div>
          <CommunityChat />
        </div>

        <div v-else-if="currentView === 'statistics'" key="statistics">
          <h2 class="section-title text-center mb-4">CELKOVÉ STATISTIKY</h2>
          <StatsDisplay />
        </div>

        <div v-else-if="currentView === 'leaderboard'" key="leaderboard">
          <h2 class="section-title text-center mb-4">TABULKA HRÁČŮ</h2>
          <UserLeaderboard />
        </div>

        <div v-else-if="currentView === 'settings'" key="settings">
          <h2 class="section-title text-center mb-4">NASTAVENÍ ÚČTU</h2>
          <UserProfileSettings />
          <div class="placeholder-section mt-4">
            <div class="icon-placeholder">
              <svg
                class="icon-lg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </div>
            <h3>ZMĚNA HESLA</h3>
            <p>Funkce změny hesla bude brzy dostupná.</p>
          </div>
        </div>
      </transition>
    </div>
    <InstallPwaAlert v-model="shouldShowPwaPrompt" />
  </div>
</template>

<script>
import HamburgerMenu from "./HamburgerMenu.vue";
import TipForm from "./TipForm.vue";
import TipStatistics from "./TipStatistics.vue";
import UserLeaderboard from "./UserLeaderboard.vue";
import StatsDisplay from "./StatsDisplay.vue";
import UserProfileSettings from "./UserProfileSettings.vue";
import CommunityChat from "./CommunityChat.vue";
import InstallPwaAlert from "./InstallPwaAlert.vue";

import { db, auth, messaging } from "../auth";
import {
  doc,
  getDoc,
  onSnapshot,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { getToken, onMessage } from "firebase/messaging";

export default {
  name: "UserAccount",
  components: {
    HamburgerMenu,
    TipForm,
    TipStatistics,
    UserLeaderboard,
    InstallPwaAlert,
    StatsDisplay,
    UserProfileSettings,
    CommunityChat,
  },
  data() {
    return {
      username: "",
      profilePicture: null,
      totalPoints: 0,
      jokerGlobalUsed: false,

      selectedRound: null,
      userId: null,
      disableTips: false,
      currentView: "dashboard",

      userUnsubscribe: null,
      chatUnsubscribe: null,

      missingTipsCount: 0,
      missingQuestion: false,
      hasUnreadMessages: false,
      lastChatVisit: null,

      notificationsEnabled: false,
      // false on iOS Safari — no PushManager / Notification API
      isNotificationSupported:
        typeof window !== "undefined" &&
        "Notification" in window &&
        "PushManager" in window,
      shouldShowPwaPrompt: false,
    };
  },
  async mounted() {
    // Guard: Notification API neexistuje na iOS Safari (jen na installed PWA od iOS 16.4+)
    if ("Notification" in window && Notification.permission === "granted") {
      this.notificationsEnabled = true;
    }

    // Guard: FCM messaging je null na nepodporovaných prohlížečích
    if (messaging) {
      this.listenToForegroundMessages();
    }

    // PWA install prompt: show every 3rd visit, only when installable
    this._evaluatePwaPrompt();
    // Fallback: beforeinstallprompt may fire after mount on first page load
    window.addEventListener(
      "beforeinstallprompt",
      (e) => {
        e.preventDefault();
        window.deferredPrompt = e;
        localStorage.setItem("pwa_installed", "false");
        this._evaluatePwaPrompt();
      },
      { once: true }
    );
    // Hide banner immediately if user installs while on this page
    window.addEventListener("appinstalled", () => {
      this.shouldShowPwaPrompt = false;
    });

    onAuthStateChanged(auth, async (user) => {
      if (user) {
        this.userId = user.uid;
        this.subscribeToUser();
        this.subscribeToChat();
        await this.fetchVisibleRound();
      } else {
        this.$router.push("/login");
      }
    });
  },
  beforeUnmount() {
    if (this.userUnsubscribe) this.userUnsubscribe();
    if (this.chatUnsubscribe) this.chatUnsubscribe();
  },
  methods: {
    _evaluatePwaPrompt() {
      // Never show in standalone/installed mode
      if (window.matchMedia("(display-mode: standalone)").matches) return;
      if (window.navigator.standalone === true) return;
      // Never show if dismissed this session
      if (sessionStorage.getItem("pwa_prompt_dismissed") === "true") return;
      // Never show if no install prompt available
      if (!window.deferredPrompt) return;
      // Never show if already installed
      if (localStorage.getItem("pwa_installed") === "true") return;

      const count =
        parseInt(localStorage.getItem("pwa_visit_count") || "0") + 1;
      localStorage.setItem("pwa_visit_count", String(count));

      if (count % 3 === 0) {
        this.shouldShowPwaPrompt = true;
      }
    },

    navigateTo(view) {
      this.currentView = view;
      if (view === "chat") {
        this.hasUnreadMessages = false;
      }
    },

    refreshStatistics() {
      if (
        this.$refs.tipStats &&
        typeof this.$refs.tipStats.fetchTipStatistics === "function"
      ) {
        this.$refs.tipStats.fetchTipStatistics();
      }
    },

    onTipsSubmitted() {
      this.refreshStatistics();
      this.checkStatus();
      setTimeout(() => {
        this.currentView = "dashboard";
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 1500);
    },

    subscribeToUser() {
      if (!this.userId) return;
      const userRef = doc(db, "users", this.userId);
      this.userUnsubscribe = onSnapshot(
        userRef,
        (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            this.username = data.username || "User";
            this.profilePicture = data.profilePicture || null;
            this.totalPoints = data.totalPoints || 0;
            this.jokerGlobalUsed = data.jokerUsed || false;

            this.lastChatVisit = data.lastChatVisit
              ? data.lastChatVisit.toDate()
              : new Date(0);
          }
        },
        (error) => {
          console.error("Chyba User:", error);
        }
      );
    },

    subscribeToChat() {
      const q = query(
        collection(db, "chat_messages"),
        orderBy("createdAt", "desc"),
        limit(1)
      );
      this.chatUnsubscribe = onSnapshot(q, (snapshot) => {
        if (!snapshot.empty) {
          const lastMsg = snapshot.docs[0].data();
          if (lastMsg.createdAt) {
            const lastMsgTime = lastMsg.createdAt.toDate();
            if (
              this.currentView !== "chat" &&
              this.lastChatVisit &&
              lastMsgTime > this.lastChatVisit
            ) {
              this.hasUnreadMessages = true;
            }
          }
        }
      });
    },

    async fetchVisibleRound() {
      try {
        const roundRef = doc(db, "settings", "visibleRound");
        const roundSnap = await getDoc(roundRef);
        if (roundSnap.exists()) {
          this.selectedRound = roundSnap.data().roundNumber;
          await this.checkStatus();
        }
      } catch (error) {
        console.error("Chyba kola:", error);
      }
    },

    async checkStatus() {
      if (!this.userId || !this.selectedRound) return;

      try {
        const matchesRef = collection(db, "matches");
        const qMatches = query(
          matchesRef,
          where("roundNumber", "==", this.selectedRound)
        );
        const matchesSnap = await getDocs(qMatches);
        const matchIds = matchesSnap.docs.map((doc) => doc.id);

        if (matchIds.length > 0) {
          const tipsRef = collection(db, "tips");
          const qTips = query(
            tipsRef,
            where("user", "==", this.userId),
            where("roundNumber", "==", this.selectedRound)
          );

          const tipsSnap = await getDocs(qTips);
          let tippedMatchIds = [];

          if (!tipsSnap.empty) {
            const tipDocData = tipsSnap.docs[0].data();
            if (tipDocData.tips && typeof tipDocData.tips === "object") {
              tippedMatchIds = Object.keys(tipDocData.tips);
            }

            if (tipDocData.questionAnswer) {
              this.missingQuestion = false;
            } else {
              await this.checkIfQuestionExists();
            }
          } else {
            this.missingTipsCount = matchIds.length;
            await this.checkIfQuestionExists();
          }

          this.missingTipsCount = matchIds.filter(
            (id) => !tippedMatchIds.includes(id)
          ).length;
        } else {
          this.missingTipsCount = 0;
        }
      } catch (error) {
        console.error("Chyba checkStatus:", error);
      }
    },

    async checkIfQuestionExists() {
      if (!this.selectedRound) return;

      try {
        const questionsRef = collection(db, "questions");
        const qQuestions = query(
          questionsRef,
          where("roundNumber", "==", this.selectedRound)
        );
        const questionsSnap = await getDocs(qQuestions);
        this.missingQuestion = !questionsSnap.empty;
      } catch (error) {
        console.error("Chyba kontroly otázky:", error);
      }
    },

    // --- METODY PRO NOTIFIKACE ---

    async enableNotifications() {
      if (!this.isNotificationSupported || !messaging) {
        alert(
          "Váš prohlížeč nepodporuje push notifikace. Na iPhone nainstalujte aplikaci přes 'Přidat na plochu'."
        );
        return;
      }
      try {
        console.log("Žádám o povolení notifikací...");
        const permission = await Notification.requestPermission();

        if (permission === "granted") {
          console.log("Povolení uděleno.");

          // Nastavíme stav na true, aby tlačítko zmizelo
          this.notificationsEnabled = true;

          const token = await getToken(messaging, {
            vapidKey:
              "BJVTuCsAXNcC6CEAp2bDXdBj0YA71j9KrwbkxdDFjpnq0QqaNwmCjQILnjqV7w8HRiG5-39xhzINKq_h7DHxQl0",
          });

          if (token) {
            console.log("Můj FCM Token:", token);
            await this.saveTokenToFirestore(token);
            alert("Notifikace byly úspěšně zapnuty! 🔔");
          }
        } else {
          alert(
            "Notifikace byly zamítnuty. Povolte je v nastavení prohlížeče."
          );
        }
      } catch (error) {
        console.error("Chyba notifikací:", error);
      }
    },

    async saveTokenToFirestore(token) {
      if (!auth.currentUser) return;
      const userRef = doc(db, "users", auth.currentUser.uid);
      await updateDoc(userRef, {
        fcmTokens: arrayUnion(token),
      });
    },

    listenToForegroundMessages() {
      if (!messaging) return;
      onMessage(messaging, (payload) => {
        console.log("Zpráva v popředí:", payload);
        alert(`🔔 ${payload.notification.title}\n${payload.notification.body}`);
      });
    },
  },
};
</script>

<style scoped>
/* NOVÝ STYL PRO TLAČÍTKO NOTIFIKACÍ */
.btn-notification {
  background: transparent;
  border: 1px solid #ffc107;
  color: #ffc107;
  padding: 10px 20px;
  border-radius: 30px;
  font-weight: bold;
  font-size: 0.9rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  margin-top: 15px;
}

.btn-notification:hover {
  background: rgba(255, 193, 7, 0.1);
  box-shadow: 0 0 15px rgba(255, 193, 7, 0.3);
  transform: translateY(-2px);
}

/* Ostatní styly zůstávají beze změn */
.avatar-header-wrapper {
  width: 100px;
  height: 100px;
  margin: 0 auto;
  border-radius: 50%;
  border: 3px solid #333;
  background-color: #000;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 15px rgba(220, 53, 69, 0.3);
}
.header-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.header-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #111;
}
.placeholder-icon {
  width: 50px;
  height: 50px;
  stroke: #444;
}
.mini-stats-badges {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 10px;
  flex-wrap: wrap;
}
.badge-pill {
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: bold;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
}
.badge-pill.points {
  background: #333;
  border: 1px solid #555;
  color: #ffc107;
}
.badge-pill.joker {
  background: #111;
  border: 1px solid #28a745;
  color: #28a745;
}
.badge-pill.joker.used {
  border-color: #555;
  color: #777;
  text-decoration: line-through;
}
.text-red {
  color: #dc3545;
}
.alert-card {
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 20px;
  max-width: 600px;
  margin: 0 auto;
}
.alert-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}
.alert-content h4 {
  font-size: 1.1rem;
  font-weight: bold;
  margin-bottom: 5px;
  text-transform: uppercase;
}
.alert-content p {
  color: #ddd;
  font-size: 0.9rem;
  margin-bottom: 5px;
}
.alert-card.warning {
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid #ffc107;
  box-shadow: 0 0 20px rgba(255, 193, 7, 0.15);
}
.warning .alert-content h4 {
  color: #ffc107;
}
.warning .alert-icon {
  color: #ffc107;
}
.alert-card.success {
  background: rgba(40, 167, 69, 0.1);
  border: 1px solid #28a745;
  box-shadow: 0 0 20px rgba(40, 167, 69, 0.15);
}
.success .alert-content h4 {
  color: #28a745;
}
.success .alert-icon {
  color: #28a745;
}
.alert-card.info {
  background: rgba(23, 162, 184, 0.1);
  border: 1px solid #17a2b8;
  box-shadow: 0 0 20px rgba(23, 162, 184, 0.15);
}
.info .alert-content h4 {
  color: #17a2b8;
}
.info .alert-icon {
  color: #17a2b8;
}
.btn-warning-glow {
  display: inline-block;
  background: #ffc107;
  color: #000;
  padding: 8px 20px;
  border: none;
  border-radius: 20px;
  font-weight: bold;
  text-transform: uppercase;
  font-size: 0.85rem;
  transition: all 0.3s;
  margin-top: 5px;
  cursor: pointer;
}
.btn-warning-glow:hover {
  background: #ffdb72;
  box-shadow: 0 0 10px rgba(255, 193, 7, 0.5);
  transform: translateY(-2px);
}
.btn-success-glow {
  display: inline-block;
  background: #28a745;
  color: #fff;
  padding: 8px 20px;
  border: none;
  border-radius: 20px;
  font-weight: bold;
  text-transform: uppercase;
  font-size: 0.85rem;
  transition: all 0.3s;
  margin-top: 5px;
  cursor: pointer;
}
.btn-success-glow:hover {
  background: #34ce57;
  box-shadow: 0 0 10px rgba(40, 167, 69, 0.5);
  transform: translateY(-2px);
}
.btn-info-glow {
  display: inline-block;
  background: #17a2b8;
  color: #fff;
  padding: 8px 20px;
  border: none;
  border-radius: 20px;
  font-weight: bold;
  text-transform: uppercase;
  font-size: 0.85rem;
  transition: all 0.3s;
  margin-top: 5px;
  cursor: pointer;
}
.btn-info-glow:hover {
  background: #1ebcd2;
  box-shadow: 0 0 10px rgba(23, 162, 184, 0.5);
  transform: translateY(-2px);
}
.btn-back {
  background: transparent;
  border: 1px solid #444;
  color: #aaa;
  padding: 8px 15px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: bold;
  transition: all 0.2s;
}
.btn-back:hover {
  background: #222;
  color: #fff;
  border-color: #666;
}
.placeholder-section {
  text-align: center;
  padding: 40px 20px;
  background: #111;
  border: 1px solid #333;
  border-radius: 12px;
}
.placeholder-section h3 {
  color: #fff;
  font-weight: bold;
  text-transform: uppercase;
  margin-bottom: 10px;
  font-size: 1.2rem;
}
.placeholder-section p {
  color: #888;
  margin: 0;
}
.icon-lg {
  width: 48px;
  height: 48px;
  stroke: #555;
  margin-bottom: 20px;
}
.icon-sm {
  width: 18px;
  height: 18px;
}
.icon-xs {
  width: 14px;
  height: 14px;
  display: inline-block;
  vertical-align: text-top;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
